/**
 * Catálogo mínimo de permissões (RBAC) alinhado a:
 * - Rotas em App.js (filhas de Layout /)
 * - Itens em components/Layout/Sidebar.js
 * - Prefixos usados em services/api.js → gateway → microserviços (/api/v1/...)
 *
 * Convenção: "domínio:ação" — estável para JWT, user_roles.permissions (auth-service) e Depends no backend.
 */

/** @typedef {string} Permission */

export const P = {
  DASHBOARD_VIEW: 'dashboard:view',

  /** Página /licenses (lista + cadastro completo em modal). /license/licencas redireciona para /licenses. */
  LICENSES_PAGE: 'licenses:page',

  ENTERPRISES_VIEW: 'enterprises:view',
  MAP_VIEW: 'map:view',
  REPORTS_VIEW: 'reports:view',
  MONITORING_ACCESS: 'monitoring:access',
  NOTIFICATIONS_MANAGE: 'notifications:manage',

  USERS_MANAGE: 'users:manage',
  SETTINGS_MANAGE: 'settings:manage',
  PLATFORM_MANAGE: 'platform:manage',

  /** Sempre permitido a usuário autenticado no MVP (pode fixar como true no guard) */
  PROFILE_VIEW: 'profile:view',

  CATALOG_ACCESS: 'catalog:access',
  CONTACT_ACCESS: 'contact:access',
  IDENTITY_ACCESS: 'identity:access',
  PROFESSIONAL_ACCESS: 'professional:access',
  COMPLAINT_ACCESS: 'complaint:access',
  INSTITUTION_ACCESS: 'institution:access',
  PROCESS_ACCESS: 'process:access',

  LICENSE_TIPOS: 'license:tipos-licenca',
  LICENSE_LICENCAS: 'license:licencas',
  LICENSE_ASSISTIDA: 'license:assistida',

  AUDIT_VIEW: 'audit:view',
  AUDIT_RESTORE: 'audit:restore',
};

/** Lista única para validação ou seeds */
export const ALL_PERMISSIONS = Object.freeze(Object.values(P));

/** Super admin sem organização: apenas console da plataforma e perfil. */
export const PLATFORM_SUPER_ADMIN_PERMISSIONS = Object.freeze([
  P.PLATFORM_MANAGE,
  P.PROFILE_VIEW,
]);

/**
 * Regras por prefixo de pathname (React Router).
 * Ordem importa: prefixos mais longos primeiro (ex.: assistida antes de licencas).
 * @type {{ prefix: string, permission: Permission }[]}
 */
export const ROUTE_PERMISSION_RULES = Object.freeze([
  { prefix: '/license/conditions', permission: P.LICENSE_LICENCAS },
  { prefix: '/licenses/assisted', permission: P.LICENSE_ASSISTIDA },
  { prefix: '/license/regularization-plans', permission: P.LICENSES_PAGE },
  { prefix: '/license/submodules', permission: P.LICENSE_LICENCAS },
  { prefix: '/license/types', permission: P.LICENSE_TIPOS },
  { prefix: '/legislation', permission: P.LICENSE_ASSISTIDA },
  { prefix: '/portal', permission: P.LICENSES_PAGE },
  { prefix: '/portfolio', permission: P.IDENTITY_ACCESS },
  { prefix: '/inspections', permission: P.COMPLAINT_ACCESS },

  { prefix: '/catalog', permission: P.CATALOG_ACCESS },
  { prefix: '/contact', permission: P.CONTACT_ACCESS },
  { prefix: '/identity', permission: P.IDENTITY_ACCESS },
  { prefix: '/professional', permission: P.PROFESSIONAL_ACCESS },
  { prefix: '/complaint', permission: P.COMPLAINT_ACCESS },
  { prefix: '/institution', permission: P.INSTITUTION_ACCESS },
  { prefix: '/process', permission: P.PROCESS_ACCESS },
  { prefix: '/requerimentos', permission: P.PROCESS_ACCESS },
  { prefix: '/pedidos', permission: P.PROCESS_ACCESS },

  { prefix: '/me', permission: P.DASHBOARD_VIEW },
  { prefix: '/assistant', permission: P.DASHBOARD_VIEW },
  { prefix: '/licenses', permission: P.LICENSES_PAGE },
  { prefix: '/files', permission: P.LICENSES_PAGE },
  { prefix: '/enterprises', permission: P.ENTERPRISES_VIEW },
  { prefix: '/map', permission: P.MONITORING_ACCESS },
  { prefix: '/reports', permission: P.REPORTS_VIEW },
  { prefix: '/notifications/templates', permission: P.NOTIFICATIONS_MANAGE },
  { prefix: '/notifications/send', permission: P.NOTIFICATIONS_MANAGE },
  { prefix: '/notifications', permission: P.MONITORING_ACCESS },
  { prefix: '/users', permission: P.USERS_MANAGE },
  { prefix: '/history', permission: P.AUDIT_VIEW },
  { prefix: '/trash', permission: P.AUDIT_RESTORE },
  { prefix: '/settings', permission: P.SETTINGS_MANAGE },
  { prefix: '/super-admin', permission: P.PLATFORM_MANAGE },
  { prefix: '/admin/email-providers', permission: P.PLATFORM_MANAGE },
  { prefix: '/admin/email-templates', permission: P.PLATFORM_MANAGE },
  { prefix: '/profile', permission: P.PROFILE_VIEW },

  { prefix: '/', permission: P.DASHBOARD_VIEW },
]);

/**
 * Prefixos HTTP do gateway (api.js) → permissão mínima sugerida para o backend.
 * Útil para espelhar o mesmo string em FastAPI / policies.
 * @type {{ apiPrefix: string, permission: Permission, service: string }[]}
 */
export const API_PREFIX_PERMISSION_HINTS = Object.freeze([
  { apiPrefix: '/api/v1/assistant', permission: P.DASHBOARD_VIEW, service: 'api-gateway (assistente)' },
  { apiPrefix: '/api/v1/me', permission: P.DASHBOARD_VIEW, service: 'api-gateway (tarefas do servidor)' },
  { apiPrefix: '/api/v1/dashboard', permission: P.DASHBOARD_VIEW, service: 'license-service (dashboard agregado)' },
  { apiPrefix: '/api/v1/notifications/in-app', permission: P.MONITORING_ACCESS, service: 'notification-service' },
  { apiPrefix: '/api/v1/notifications/status', permission: P.MONITORING_ACCESS, service: 'notification-service' },
  { apiPrefix: '/api/v1/notifications/types', permission: P.NOTIFICATIONS_MANAGE, service: 'notification-service' },
  { apiPrefix: '/api/v1/notifications/templates', permission: P.NOTIFICATIONS_MANAGE, service: 'notification-service' },
  { apiPrefix: '/api/v1/notifications/send', permission: P.NOTIFICATIONS_MANAGE, service: 'notification-service' },
  { apiPrefix: '/api/v1/notifications/email', permission: P.NOTIFICATIONS_MANAGE, service: 'notification-service' },
  { apiPrefix: '/api/v1/notifications/whatsapp', permission: P.NOTIFICATIONS_MANAGE, service: 'notification-service' },
  { apiPrefix: '/api/v1/notifications/sms', permission: P.NOTIFICATIONS_MANAGE, service: 'notification-service' },
  { apiPrefix: '/api/v1/admin/notifications/email-templates', permission: P.PLATFORM_MANAGE, service: 'notification-service' },

  { apiPrefix: '/api/v1/company-sizes', permission: P.CATALOG_ACCESS, service: 'catalog-service' },
  { apiPrefix: '/api/v1/company-statuses', permission: P.CATALOG_ACCESS, service: 'catalog-service' },
  { apiPrefix: '/api/v1/economic-activities', permission: P.CATALOG_ACCESS, service: 'catalog-service' },

  { apiPrefix: '/api/v1/contact/contacts', permission: P.CONTACT_ACCESS, service: 'contact-service' },
  { apiPrefix: '/api/v1/positions', permission: P.CONTACT_ACCESS, service: 'contact-service' },

  { apiPrefix: '/api/v1/identity/persons', permission: P.IDENTITY_ACCESS, service: 'identity-service' },
  { apiPrefix: '/api/v1/companies', permission: P.IDENTITY_ACCESS, service: 'identity-service' },
  { apiPrefix: '/api/v1/identity/clients', permission: P.IDENTITY_ACCESS, service: 'identity-service' },
  { apiPrefix: '/api/v1/identity/enterprises', permission: P.IDENTITY_ACCESS, service: 'identity-service' },

  { apiPrefix: '/api/v1/technical-responsibles', permission: P.PROFESSIONAL_ACCESS, service: 'professional-service' },
  { apiPrefix: '/api/v1/professional-councils', permission: P.PROFESSIONAL_ACCESS, service: 'professional-service' },
  { apiPrefix: '/api/v1/professional-formations', permission: P.PROFESSIONAL_ACCESS, service: 'professional-service' },

  { apiPrefix: '/api/v1/complaint/', permission: P.COMPLAINT_ACCESS, service: 'complaint-service' },

  { apiPrefix: '/api/v1/institution/', permission: P.INSTITUTION_ACCESS, service: 'institution-service' },

  { apiPrefix: '/api/v1/process/', permission: P.PROCESS_ACCESS, service: 'process-service' },

  { apiPrefix: '/api/v1/license/tipos-licenca', permission: P.LICENSE_TIPOS, service: 'license-service' },
  { apiPrefix: '/api/v1/license/licencas', permission: P.LICENSE_LICENCAS, service: 'license-service' },
  { apiPrefix: '/api/v1/license/condicionantes', permission: P.LICENSE_LICENCAS, service: 'license-service' },
  { apiPrefix: '/api/v1/license/condicionante-anexos', permission: P.LICENSE_LICENCAS, service: 'license-service' },
  { apiPrefix: '/api/v1/license/licenca-anexos', permission: P.LICENSE_LICENCAS, service: 'license-service' },
  { apiPrefix: '/api/v1/license/eventos', permission: P.LICENSE_LICENCAS, service: 'license-service' },
  { apiPrefix: '/api/v1/license/horarios-evento', permission: P.LICENSE_LICENCAS, service: 'license-service' },
  { apiPrefix: '/api/v1/license/tipos-evento', permission: P.LICENSE_LICENCAS, service: 'license-service' },
  { apiPrefix: '/api/v1/license/tipos-certidao', permission: P.LICENSE_LICENCAS, service: 'license-service' },
  { apiPrefix: '/api/v1/license/solicitacoes', permission: P.LICENSE_LICENCAS, service: 'license-service' },
  { apiPrefix: '/api/v1/license/certidoes-viabilidade', permission: P.LICENSE_LICENCAS, service: 'license-service' },
  { apiPrefix: '/api/v1/license/documentos-licenca', permission: P.LICENSE_LICENCAS, service: 'license-service' },

  { apiPrefix: '/api/v1/identity/enterprise-fotos', permission: P.IDENTITY_ACCESS, service: 'identity-service' },

  { apiPrefix: '/api/v1/legislation', permission: P.LICENSE_ASSISTIDA, service: 'legislation-service' },
]);

/**
 * Fonte única: ambiental-service/shared/role_permissions.json
 * Sugestão de mapeamento role_name (auth-service user_roles) → permissões.
 * @type {Record<string, Permission[]>}
 */
export const ROLE_PERMISSIONS_SUGGESTION = Object.freeze({
  super_admin: [...ALL_PERMISSIONS],
  tenant_admin: [...ALL_PERMISSIONS],

  gestor_ambiental: Object.freeze([
    P.DASHBOARD_VIEW,
    P.LICENSES_PAGE,
    P.ENTERPRISES_VIEW,
    P.MAP_VIEW,
    P.MONITORING_ACCESS,
    P.NOTIFICATIONS_MANAGE,
    P.REPORTS_VIEW,
    P.PROFILE_VIEW,
    P.IDENTITY_ACCESS,
    P.PROCESS_ACCESS,
    P.LICENSE_TIPOS,
    P.LICENSE_LICENCAS,
    P.LICENSE_ASSISTIDA,
    P.COMPLAINT_ACCESS,
    P.PROFESSIONAL_ACCESS,
    P.INSTITUTION_ACCESS,
    P.SETTINGS_MANAGE,
    P.AUDIT_VIEW,
  ]),

  analista: Object.freeze([
    P.DASHBOARD_VIEW,
    P.LICENSES_PAGE,
    P.ENTERPRISES_VIEW,
    P.MAP_VIEW,
    P.MONITORING_ACCESS,
    P.PROFILE_VIEW,
    P.IDENTITY_ACCESS,
    P.PROCESS_ACCESS,
    P.LICENSE_LICENCAS,
    P.LICENSE_ASSISTIDA,
    P.CATALOG_ACCESS,
    P.CONTACT_ACCESS,
    P.AUDIT_VIEW,
  ]),

  consulta: Object.freeze([
    P.DASHBOARD_VIEW,
    P.LICENSES_PAGE,
    P.ENTERPRISES_VIEW,
    P.MAP_VIEW,
    P.MONITORING_ACCESS,
    P.NOTIFICATIONS_MANAGE,
    P.REPORTS_VIEW,
    P.PROFILE_VIEW,
  ]),

  denuncias: Object.freeze([P.DASHBOARD_VIEW, P.PROFILE_VIEW, P.COMPLAINT_ACCESS]),
  fiscal: Object.freeze([
    P.DASHBOARD_VIEW,
    P.PROFILE_VIEW,
    P.COMPLAINT_ACCESS,
    P.MAP_VIEW,
    P.MONITORING_ACCESS,
    P.LICENSES_PAGE,
  ]),

  /** Usuário vinculado à empresa — vê todos empreendimentos da company_id no JWT */
  empresa: Object.freeze([
    P.DASHBOARD_VIEW,
    P.LICENSES_PAGE,
    P.ENTERPRISES_VIEW,
    P.MAP_VIEW,
    P.MONITORING_ACCESS,
    P.PROFILE_VIEW,
    P.IDENTITY_ACCESS,
    P.LICENSE_LICENCAS,
    P.PROCESS_ACCESS,
  ]),

  /** Usuário vinculado a um empreendimento — escopo filtrado por enterprise_id no JWT */
  empreendimento: Object.freeze([
    P.DASHBOARD_VIEW,
    P.LICENSES_PAGE,
    P.ENTERPRISES_VIEW,
    P.MAP_VIEW,
    P.MONITORING_ACCESS,
    P.PROFILE_VIEW,
    P.LICENSE_LICENCAS,
    P.PROCESS_ACCESS,
  ]),

  /** Pessoa física no portal — só o que é dela, sem visão operacional do órgão */
  requerente: Object.freeze([
    P.LICENSES_PAGE,
    P.ENTERPRISES_VIEW,
    P.PROFILE_VIEW,
    P.IDENTITY_ACCESS,
    P.LICENSE_LICENCAS,
    P.PROCESS_ACCESS,
    P.MONITORING_ACCESS,
  ]),

  consultor: Object.freeze([
    P.DASHBOARD_VIEW,
    P.LICENSES_PAGE,
    P.ENTERPRISES_VIEW,
    P.MAP_VIEW,
    P.MONITORING_ACCESS,
    P.PROFILE_VIEW,
    P.IDENTITY_ACCESS,
    P.PROCESS_ACCESS,
    P.LICENSE_LICENCAS,
    P.AUDIT_VIEW,
  ]),

  coordenador: Object.freeze([
    P.DASHBOARD_VIEW,
    P.LICENSES_PAGE,
    P.ENTERPRISES_VIEW,
    P.MAP_VIEW,
    P.MONITORING_ACCESS,
    P.REPORTS_VIEW,
    P.PROFILE_VIEW,
    P.IDENTITY_ACCESS,
    P.PROCESS_ACCESS,
    P.LICENSE_LICENCAS,
    P.AUDIT_VIEW,
  ]),
});

/**
 * @param {string} pathname location.pathname
 * @returns {Permission | null} null = só autenticação (não usar até definir default)
 */
export function getRequiredPermissionForPath(pathname) {
  const raw = pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
  for (const rule of ROUTE_PERMISSION_RULES) {
    if (rule.prefix === '/') {
      if (raw === '/' || raw === '') return rule.permission;
      continue;
    }
    if (raw === rule.prefix || raw.startsWith(`${rule.prefix}/`)) {
      return rule.permission;
    }
  }
  return null;
}

/**
 * @param {string[]} granted
 * @param {Permission} required
 */
export function hasPermission(granted, required) {
  if (!required) return true;
  if (!Array.isArray(granted) || granted.length === 0) return false;
  if (granted.includes('*')) return true;
  return granted.includes(required);
}

/**
 * Expande roles do usuário em permissões (MVP: mapa estático).
 * Depois: substituir por lista vinda do /me ou do JWT.
 * @param {string[]} roleNames
 * @returns {string[]}
 */
export function expandRolesToPermissions(roleNames) {
  const set = new Set();
  for (const r of roleNames || []) {
    const list = ROLE_PERMISSIONS_SUGGESTION[r];
    if (list) list.forEach((p) => set.add(p));
  }
  return Array.from(set);
}

/**
 * Permissões efetivas: `user.permissions` (API futura) ou expansão de `user.roles`;
 * se não houver papéis mapeados, libera tudo (compatível com ambientes sem RBAC populado).
 * @param {{ roles?: string[], permissions?: string[], organization_id?: string | null } | null | undefined} user
 * @returns {string[]}
 */
export function resolveUserPermissions(user) {
  if (!user) return [...ALL_PERMISSIONS];
  const roles = (user.roles || []).map((r) => String(r).toLowerCase());
  const platformOnly =
    roles.includes('super_admin') &&
    (user.organization_id == null || user.organization_id === '');
  if (platformOnly) {
    return [...PLATFORM_SUPER_ADMIN_PERMISSIONS];
  }
  if (Array.isArray(user.permissions) && user.permissions.length > 0) {
    return [...user.permissions];
  }
  const expanded = expandRolesToPermissions(user.roles || []);
  if (expanded.length === 0) {
    return [P.PROFILE_VIEW];
  }
  return expanded;
}
